import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConsumerChange, DeclinedConsumer, PlannedChange, PlanView } from '../../types/agent';
import { CellText } from './CellLink';

/** The accepted Change Plan: where it writes, and what it deliberately did not touch.
 *
 *  This replaces a markdown string the server used to build from lines joined with `\n` and two leading
 *  spaces — which CommonMark folds into one paragraph — carrying the whole declined formula inline. On a
 *  thirteen-sheet model that arrived as four hundred characters of R1C1 per item, run together with the
 *  places and the call count into a single block of text filling the pane.
 *
 *  What a modeller needs from a declined formula is the cell and the shape that stopped it. The address
 *  selects it in Excel; the formula is one disclosure away, for the one case worth reading in full. The places the
 *  notes, the reasons and the mentions name are links too (`CellText`). */
export function PlanCard({ view, onShowCell, sheets }: { view: PlanView; onShowCell?: (sheet: string, cell: string) => void; sheets?: readonly string[] }) {
  const { t } = useTranslation();
  const verb = t(`plan.${view.operation}`, { name: view.name });
  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="plan-card">
      <div className="border-b border-line-row px-3 py-2">
        <div className="text-[12.5px] font-medium text-ink">{verb}</div>
        {view.modelled_on && <div className="mt-0.5 text-[11.5px] text-muted">{t('plan.modelledOn', { name: view.modelled_on })}</div>}
        {view.asked && (
          <div className="mt-0.5 text-[11.5px] text-accent" data-testid="plan-asked">
            {t('plan.asked', { asked: view.asked, name: view.modelled_on || view.name })}
          </div>
        )}
        {view.proposed && <div className="mt-0.5 text-[11.5px] text-accent" data-testid="plan-proposed">{t('plan.proposed')}</div>}
      </div>

      {view.notes.length > 0 && (
        <ul className="flex flex-col gap-0.5 border-b border-line-row px-3 py-1.5 text-[11.5px] text-muted">
          {view.notes.map((note) => (
            <li key={note}><CellText text={note} sheets={sheets} onSelectRange={onShowCell} /></li>
          ))}
        </ul>
      )}

      {view.changes.length > 0 && (
        <Section title={t('plan.where')}>
          {view.changes.map((c, i) => (
            <PlaceRow key={`${c.sheet}-${c.at}-${i}`} change={c} proposed={!!view.proposed} onShowCell={onShowCell} sheets={sheets} />
          ))}
        </Section>
      )}

      {(view.consumers ?? []).length > 0 && (
        <Section title={t('plan.consumers')}>
          {(view.consumers ?? []).map((c, i) => (
            <ConsumerRow key={`${c.sheet}!${c.today}-${i}`} item={c} proposed={!!view.proposed} onShowCell={onShowCell} />
          ))}
        </Section>
      )}

      {view.declined.length > 0 && (
        <Section title={t('plan.notExtended')} hint={t('plan.notExtendedHint')}>
          {view.declined.map((d) => (
            <DeclinedRow key={`${d.sheet}!${d.cell}`} item={d} onShowCell={onShowCell} sheets={sheets} />
          ))}
        </Section>
      )}

      {view.by_hand.length > 0 && (
        <Section title={t('plan.byHand', { name: view.name })}>
          {view.by_hand.map((mention) => (
            <div key={mention} className="py-px text-[11.5px] text-muted">
              <CellText text={mention} sheets={sheets} onSelectRange={onShowCell} />
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

/** One place the plan writes, as a link to its band. Once written, the link names and selects the band where the
 *  rows really are — "2 rows at 67" lands at 70–71 when the plan inserts three rows higher up on the sheet. A
 *  proposed plan wrote nothing, so it keeps the phrase and selects the line the rows would go in front of. A
 *  written removal, or a plan sent before the server gave places, has nothing to select. */
function PlaceRow({ change: c, proposed, onShowCell, sheets }: { change: PlannedChange; proposed: boolean; onShowCell?: (sheet: string, cell: string) => void; sheets?: readonly string[] }) {
  const { t } = useTranslation();
  const target = proposed ? c.today : c.address;
  const band = !proposed && c.address ? c.address.split(':') : null;
  const label = band
    ? t(c.axis === 'cols' ? 'plan.colsBand' : 'plan.rowsBand', { count: c.count, first: band[0], last: band[1] })
    : t(c.axis === 'cols' ? 'plan.cols' : 'plan.rows', { count: c.count, at: c.at });
  return (
    <div className="flex items-baseline gap-2 py-px text-[11.5px]" data-testid="plan-place">
      <span className="shrink-0 font-mono text-[11px] text-ink">{c.sheet}</span>
      {target && onShowCell ? (
        <button
          type="button"
          onClick={() => onShowCell(c.sheet, target)}
          title={`${c.sheet}!${target}`}
          className="shrink-0 text-accent underline-offset-2 hover:text-accent-dark hover:underline"
        >
          {label}
        </button>
      ) : (
        <span className="shrink-0 text-muted">{label}</span>
      )}
      {c.why && <span className="min-w-0 flex-1 truncate text-fainter" title={c.why}><CellText text={c.why} sheets={sheets} onSelectRange={onShowCell} /></span>}
    </div>
  );
}

/** A formula that changes with the plan, as a link to its cells: where they are once written, or today for a
 *  proposed plan, which wrote nothing. Rewritten by the plan, or widened by Excel to take the new object in. */
function ConsumerRow({ item, proposed, onShowCell }: { item: ConsumerChange; proposed: boolean; onShowCell?: (sheet: string, cell: string) => void }) {
  const { t } = useTranslation();
  const target = proposed ? item.today : item.address;
  return (
    <div className="flex items-baseline gap-2 py-px text-[11.5px]" data-testid="plan-consumer">
      {target && onShowCell ? (
        <button
          type="button"
          onClick={() => onShowCell(item.sheet, target)}
          title={`${item.sheet}!${target}`}
          className="shrink-0 font-mono text-[11px] text-accent underline-offset-2 hover:text-accent-dark hover:underline"
        >
          {item.sheet}!{target}
        </button>
      ) : (
        <span className="shrink-0 font-mono text-[11px] text-muted">{item.sheet}!{target}</span>
      )}
      {item.label && <span className="min-w-0 flex-1 truncate text-ink" title={item.label}>{item.label}</span>}
      <span className="shrink-0 text-fainter">{t(item.how === 'widened' ? 'plan.widened' : 'plan.rewritten')}</span>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-line-row px-3 py-2 last:border-b-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-fainter">{title}</div>
      {hint && <div className="mt-0.5 text-[11.5px] text-muted">{hint}</div>}
      <div className="mt-1 flex flex-col">{children}</div>
    </div>
  );
}

function DeclinedRow({ item, onShowCell, sheets }: { item: DeclinedConsumer; onShowCell?: (sheet: string, cell: string) => void; sheets?: readonly string[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line-row/60 py-1 last:border-b-0" data-testid="declined">
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!onShowCell}
          onClick={() => onShowCell?.(item.sheet, item.cell)}
          title={onShowCell ? t('action.show') : undefined}
          className={`shrink-0 font-mono text-[11px] ${onShowCell ? 'text-accent hover:text-accent-dark hover:underline' : 'cursor-default text-muted'}`}
        >
          {item.sheet}!{item.cell}
        </button>
        {item.label && <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink" title={item.label}>{item.label}</span>}
        {item.formula && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={t('plan.showFormula')}
            className="shrink-0 text-[9px] text-fainter hover:text-ink"
          >
            <span className={`inline-block transition-transform duration-150 ${open ? 'rotate-90' : ''}`}>▶</span>
          </button>
        )}
      </div>
      <div className="text-[11.5px] text-muted"><CellText text={item.reason} sheets={sheets} onSelectRange={onShowCell} /></div>
      {open && item.formula && (
        <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded-sm bg-code px-2 py-1.5 font-mono text-[11px] leading-normal text-[#2d3134]">
          {item.formula}
        </pre>
      )}
    </div>
  );
}
